import moment from "jalali-moment";
import { ReactElement, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import usePagination from "../../hooks/usePagination";
import { useResponsive } from "../../hooks/useResponsive";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import { Sidebar } from "../../layouts/Sidebar";

const Logs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>({ data: [], total: 0 });
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [method, setMethod] = useState(searchParams.get("method") || "all");
  const [userId, setUserId] = useState(searchParams.get("userId") || "");
  const [ipAddress, setIpAddress] = useState(
    searchParams.get("ipAddress") || "",
  );
  const [startDate, setStartDate] = useState(
    searchParams.get("startDate") || moment().format("jYYYY/jMM/jDD"),
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("endDate") || moment().format("jYYYY/jMM/jDD"),
  );
  const { pageCount, itemsPerPage, setPageCount, setItemsPerPage } =
    usePagination(data.data);
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();

  const methodTitles = {
    all: "همه",
    GET: "GET",
    POST: "POST",
    PUT: "PUT",
    DELETE: "DELETE",
    PATCH: "PATCH",
  };
  const [stats, setStats] = useState<any>(null);

  const list = () => {
    const rows: ReactElement[] = [];

    data?.data?.map((log: any, index: number) => {
      const currentPage = Number(searchParams.get("page")) || 1;
      const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

      rows.push(
        <tr className="dashTr2" key={"log" + index}>
          <td>
            <div className="column">
              <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
                {log.data
                  ? JSON.stringify(JSON.parse(log.data), null, 2).substring(
                      0,
                      100,
                    ) + "..."
                  : "-"}
              </code>
            </div>
          </td>
          <td>
            {log.userAgent ? log.userAgent.substring(0, 30) + "..." : "-"}
          </td>
          <td>{log.ipAddress}</td>
          <td>{log.userId || "-"}</td>
          <td>
            <span className={`methodBadge method${log.method}`}>
              {log.method}
            </span>
          </td>
          <td style={{ maxWidth: "200px", wordBreak: "break-all" }}>
            {log.pathname}
          </td>
          <td>
            <div className="column">
              <p>{log.date}</p>
              <p>{log.time}</p>
            </div>
          </td>
          <td>{rowNumber}</td>
        </tr>,
      );
    });

    return rows;
  };

  const fetchLogs = async () => {
    dispatch(setLoading(true));

    const params: any = {
      page: searchParams.get("page") || 1,
      limit: itemsPerPage,
    };

    if (query) params.pathname = query;
    if (method && method !== "all") params.method = method;
    if (userId) params.userId = userId;
    if (ipAddress) params.ipAddress = ipAddress;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    try {
      const res = await restApi(endpoints.logs.search, true).get(params);
      setData(res);
      setPageCount(
        res.data.totalPages || Math.ceil(res.data.total / itemsPerPage),
      );
    } catch (error) {
      console.error("Error fetching logs:", error);
      Swal.fire({
        title: "خطا",
        text: "خطا در دریافت لاگ‌ها",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchStats = async () => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    try {
      const res = await restApi(endpoints.logs.stats, true).get(params);
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [searchParams.get("page"), itemsPerPage, method]);

  useEffect(() => {
    if (data.total) {
      const newPage =
        Number(searchParams.get("page")) <= Math.ceil(data.total / itemsPerPage)
          ? searchParams.get("page") || "1"
          : "1";

      const params: any = { page: newPage };
      if (method !== "all") params.method = method;
      if (query) params.query = query;
      if (userId) params.userId = userId;
      if (ipAddress) params.ipAddress = ipAddress;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      setSearchParams(params);
    }
  }, [data.total]);

  const handleSearch = () => {
    setSearchParams({
      page: "1",
      ...(method !== "all" && { method }),
      ...(query && { query }),
      ...(userId && { userId }),
      ...(ipAddress && { ipAddress }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    });
    fetchLogs();
  };

  const clearFilters = () => {
    setQuery("");
    setMethod("all");
    setUserId("");
    setIpAddress("");
    setStartDate("");
    setEndDate("");
    setSearchParams({ page: "1" });
  };

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />

        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">لاگ‌های سیستم</h1>

          {/* Statistics Section */}
          {stats && (
            <div className="statsContainer">
              <div className="statCard">
                <h3>کل درخواست‌ها</h3>
                <p className="statNumber">
                  {stats.totalRequests?.toLocaleString("fa-IR")}
                </p>
              </div>
              <div className="statCard">
                <h3>کاربران یکتا</h3>
                <p className="statNumber">
                  {stats.uniqueUsers?.toLocaleString("fa-IR")}
                </p>
              </div>
              <div className="statCard">
                <h3>IP های یکتا</h3>
                <p className="statNumber">
                  {stats.uniqueIPs?.toLocaleString("fa-IR")}
                </p>
              </div>
              {stats.methods &&
                Object.entries(stats.methods).map(
                  ([method, count]: [string, any]) => (
                    <div className="statCard" key={method}>
                      <h3>{method}</h3>
                      <p className="statNumber">
                        {count?.toLocaleString("fa-IR")}
                      </p>
                    </div>
                  ),
                )}
            </div>
          )}

          {/* Method Tabs */}
          <div className="dashTabs">
            {Object.entries(methodTitles).map(([key, value]) => (
              <span
                key={key}
                className={`ordersTag clickable ${key === method ? "activeTab" : ""}`}
                onClick={() => {
                  setMethod(key);
                  setSearchParams({
                    page: "1",
                    method: key,
                    ...(query && { query }),
                    ...(userId && { userId }),
                    ...(ipAddress && { ipAddress }),
                    ...(startDate && { startDate }),
                    ...(endDate && { endDate }),
                  });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setMethod(key);
                    setSearchParams({ page: "1", method: key });
                  }
                }}
              >
                {value}
                {stats?.methods && key !== "all" && (
                  <span
                    className={`numberTag ${key === method ? "activeTab" : ""}`}
                  >
                    {stats.methods[key]?.toLocaleString("fa-IR") || 0}
                  </span>
                )}
                {key === "all" && stats && (
                  <span
                    className={`numberTag ${key === method ? "activeTab" : ""}`}
                  >
                    {stats.totalRequests?.toLocaleString("fa-IR") || 0}
                  </span>
                )}
              </span>
            ))}
          </div>

          {/* Filters Section */}
          <div className="filtersSection">
            <div className="filterRow">
              <div className="dashboardseaechBox">
                <i className="dashMagnifierIcon"></i>
                <input
                  className="dashSearchInput"
                  placeholder="جستجو در مسیر (pathname)"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="جستجوی مسیر"
                />
              </div>

              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="شناسه کاربر"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  aria-label="شناسه کاربر"
                />
              </div>

              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="آدرس IP"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  aria-label="آدرس IP"
                />
              </div>
            </div>

            <div className="filterRow">
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="از تاریخ (مثال: 1403/09/01)"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="از تاریخ"
                />
              </div>

              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="تا تاریخ (مثال: 1403/09/30)"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="تا تاریخ"
                />
              </div>

              <button className="searchButton" onClick={handleSearch}>
                <i className="dashMagnifierIcon"></i>
                جستجو
              </button>

              <button className="clearButton" onClick={clearFilters}>
                پاک کردن فیلترها
              </button>
            </div>
          </div>

          <div className="searchContainer">
            <span className="backSpan keepRight">
              <span className="dashboardHeader">
                <p>تعداد کل: {data.total?.toLocaleString("fa-IR")}</p>
              </span>
            </span>

            <div className="itemsPerPage">
              <label htmlFor="itemPerPage">تعداد در صفحه</label>
              <input
                id="itemPerPage"
                type="number"
                min="1"
                max="100"
                defaultValue={itemsPerPage}
                onChange={(input) => {
                  const value = Number(input.target.value);
                  if (value && value > 0 && value <= 100) {
                    setItemsPerPage(value);
                  }
                }}
                onBlur={(e) => {
                  if (!e.target.value || Number(e.target.value) <= 0) {
                    e.target.value = itemsPerPage.toString();
                  }
                }}
              />
            </div>
          </div>

          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                page: (Number(event.selected) + 1).toString(),
                ...(method !== "all" && { method }),
                ...(query && { query }),
                ...(userId && { userId }),
                ...(ipAddress && { ipAddress }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
              });
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />

          <div className="table-container">
            <table>
              <thead>
                <tr className="dashTr1 blueText">
                  <th>داده</th>
                  <th>User Agent</th>
                  <th>IP</th>
                  <th>شناسه کاربر</th>
                  <th>متد</th>
                  <th>مسیر</th>
                  <th>تاریخ و زمان</th>
                  <th>ردیف</th>
                </tr>
              </thead>
              <tbody>{list()}</tbody>
            </table>
          </div>

          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                page: (Number(event.selected) + 1).toString(),
                ...(method !== "all" && { method }),
                ...(query && { query }),
                ...(userId && { userId }),
                ...(ipAddress && { ipAddress }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
              });
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />
        </main>
      </div>
    </>
  );
};

export default Logs;
